import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { API } from '../App';

export default function AuthPage({ onAuth }) {
  const [mode, setMode]       = useState('login'); // 'login' | 'forgot' | 'forgot-sent' | 'request' | 'pending'
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  function reset(newMode) { setMode(newMode); setError(''); setEmail(''); setPassword(''); }

  async function submitLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && data.error === 'pending') {
          setError(data.message || 'Your account is pending approval.');
        } else {
          setError(data.error || 'Something went wrong');
        }
        setLoading(false); return;
      }
      const storage = remember ? localStorage : sessionStorage;
      storage.setItem('ic_token', data.token);
      storage.setItem('ic_user', JSON.stringify(data.user));
      onAuth(data.user, data.token);
    } catch { setError('Network error — please try again'); }
    setLoading(false);
  }

  async function submitForgot(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setMode('forgot-sent');
    } catch { setError('Network error — please try again'); }
    setLoading(false);
  }

  async function submitRequest(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong'); setLoading(false); return; }
      setMode('pending');
    } catch { setError('Network error — please try again'); }
    setLoading(false);
  }

  return (
    <div style={{
      width: '100vw', height: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)',
    }}>
      <motion.div
        key={mode}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          width: '100%', maxWidth: 400,
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: 20,
          padding: '40px 36px',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/ideacanvas-logo.png" alt="IdeaCanvas" style={{ width: 120, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {mode === 'login'       ? 'Welcome back'
           : mode === 'forgot'      ? 'Reset your password'
           : mode === 'request'     ? 'Request access'
           : ''}
          </p>
        </div>

        {/* ── PENDING ── */}
        {mode === 'pending' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🎉</div>
            <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary)', fontSize: 18 }}>You're on the list!</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              Your request has been submitted. You'll receive an email with a link to set your password once you're approved.
            </p>
            <button onClick={() => reset('login')} style={linkBtn}>Back to sign in</button>
          </div>
        )}

        {/* ── FORGOT SENT ── */}
        {mode === 'forgot-sent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>📬</div>
            <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary)', fontSize: 18 }}>Check your inbox</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              If that email has an account, we've sent a reset link. It expires in 1 hour.
            </p>
            <button onClick={() => reset('login')} style={linkBtn}>Back to sign in</button>
          </div>
        )}

        {/* ── LOGIN ── */}
        {mode === 'login' && (
          <form onSubmit={submitLogin}>
            <Field label="Email" type="email" value={email} onChange={value => { setEmail(value); setError(''); }} placeholder="you@example.com" autoFocus />
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Password</label>
                <button type="button" onClick={() => reset('forgot')} style={{ ...linkBtn, fontSize: 12 }}>
                  Forgot password?
                </button>
              </div>
              <input
                type="password" required
                value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder="••••••••"
                style={inputStyle}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, marginTop: 14, cursor: 'pointer' }}>
              <input
                type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Remember me</span>
            </label>
            <ErrorBox msg={error} />
            <SubmitBtn loading={loading}>Sign in</SubmitBtn>
          </form>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {mode === 'forgot' && (
          <form onSubmit={submitForgot}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px', lineHeight: 1.6 }}>
              Enter your email and we'll send you a reset link.
            </p>
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
            <ErrorBox msg={error} />
            <SubmitBtn loading={loading}>Send reset link</SubmitBtn>
          </form>
        )}

        {/* ── REQUEST ACCESS ── */}
        {mode === 'request' && (
          <form onSubmit={submitRequest}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px', lineHeight: 1.6 }}>
              IdeaCanvas is currently invite-only. Enter your email and we'll reach out when you're approved.
            </p>
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
            <ErrorBox msg={error} />
            <SubmitBtn loading={loading}>Request access</SubmitBtn>
          </form>
        )}

        {/* ── Footer links ── */}
        {(mode === 'login' || mode === 'forgot') && (
          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-muted)' }}>
            {mode === 'login' ? (
              <>Don't have an account?{' '}
                <button onClick={() => reset('request')} style={linkBtn}>Request access</button>
              </>
            ) : (
              <button onClick={() => reset('login')} style={linkBtn}>Back to sign in</button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function Field({ label, type = 'text', value, onChange, placeholder, autoFocus }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
        {label}
      </label>
      <input
        type={type} required autoFocus={autoFocus}
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      marginBottom: 16, padding: '10px 14px', borderRadius: 10,
      background: '#ef444418', border: '1px solid #ef444440',
      fontSize: 13, color: '#ef4444',
    }}>{msg}</div>
  );
}

function SubmitBtn({ loading, children }) {
  return (
    <motion.button
      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
      type="submit" disabled={loading}
      style={{
        width: '100%', padding: '12px', borderRadius: 12, border: 'none',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white', fontWeight: 700, fontSize: 15,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        fontFamily: 'inherit', marginTop: 4,
      }}
    >{loading ? '…' : children}</motion.button>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-2)',
  color: 'var(--text-primary)', fontSize: 14, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box',
};

const linkBtn = {
  background: 'none', border: 'none', color: '#6366f1',
  cursor: 'pointer', fontWeight: 600, fontSize: 13, padding: 0,
  fontFamily: 'inherit',
};
